import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { adminApi, consultingApi } from '../utils/api';
import {
  LayoutDashboard, FolderTree, Wrench, Settings, LogOut,
  Plus, Edit, Trash2, Sparkles, FileText, DollarSign, Users, X,
  SearchCode, MessageSquareQuote, Mail, FileClock, FileSearch2,
  BarChart3, LineChart, ActivitySquare, Handshake, BookOpen, UsersRound, SlidersHorizontal, Megaphone, BriefcaseBusiness, Upload, TrendingUp, ShieldCheck, RefreshCw
} from 'lucide-react';
import { motion } from 'framer-motion';
import { InfoTag } from '../components/ui/InfoTag';
import DashboardOverview from './admin/DashboardOverview';
import BlogManager from './admin/BlogManager';
import CommissionsManager from './admin/CommissionsManager';
import SubscribersManager from './admin/SubscribersManager';
import SiteSettingsPage from './admin/SiteSettingsPage';
import AEODashboard from './admin/AEODashboard';
import FAQAdminPage from './admin/FAQAdminPage';
import {
  NewsletterAdminTab,
  ChangelogsAdminTab,
  SeoPagesAdminTab,
  LoggingAdminTab,
  FitScoresAdminTab,
  PricingMonitorAdminTab,
  HealthMonitorAdminTab,
  SponsorshipsAdminTab,
  GuidesAdminTab,
  PartnersAdminTab,
  SeoSettingsAdminTab,
} from './admin/GrowthOpsTabs';

const slugify = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const TOOL_LOGO_PLACEHOLDER = '/images/tool-placeholder.svg';

const isTrackingIconProvider = (rawUrl = '') => {
  const value = String(rawUrl || '').trim();
  if (!value) return false;
  try {
    const parsed = new URL(value, window.location.origin);
    const host = (parsed.hostname || '').toLowerCase();
    const path = (parsed.pathname || '').toLowerCase();
    if (host.includes('google.com') && path.includes('/s2/favicons')) return true;
    return host.includes('logo.clearbit.com') || host.includes('gstatic.com');
  } catch {
    return false;
  }
};

const buildFirstPartyFaviconUrl = (rawUrl = '') => {
  const value = String(rawUrl || '').trim();
  if (!value) return '';
  try {
    const parsed = new URL(value.startsWith('http') ? value : `https://${value}`);
    const host = (parsed.hostname || '').replace(/^www\./, '');
    return host ? `https://${host}/favicon.ico` : '';
  } catch {
    return '';
  }
};

const getSafeToolLogoUrl = (tool = {}) => {
  const logo = String(tool.logo_url || '').trim();
  if (logo && !isTrackingIconProvider(logo)) {
    return logo;
  }
  const favicon = String(tool.favicon_url || '').trim();
  if (favicon && !isTrackingIconProvider(favicon)) {
    return favicon;
  }
  const firstParty = buildFirstPartyFaviconUrl(tool.website_url);
  return firstParty || TOOL_LOGO_PLACEHOLDER;
};

const normalizePricingTiers = (tiers) => {
  if (!Array.isArray(tiers)) return [];
  return tiers.map((tier) => ({
    name: tier?.name || 'Plan',
    price: tier?.price || '',
    features: Array.isArray(tier?.features) ? tier.features : [],
    is_popular: !!tier?.is_popular,
  }));
};

const ensureMinimumPricingTiers = (tiers, minimum = 3) => {
  const normalized = normalizePricingTiers(tiers)
    .map((tier, index) => ({
      ...tier,
      name: (tier.name || '').trim() || (index === 0 ? 'Starter' : index === 1 ? 'Pro' : index === 2 ? 'Enterprise' : `Plan ${index + 1}`),
      price: (tier.price || '').trim() || (index === 0 ? 'Free' : 'Custom'),
      features: Array.isArray(tier.features) ? tier.features.filter(Boolean) : [],
    }))
    .slice(0, 6);

  while (normalized.length < minimum) {
    const index = normalized.length;
    normalized.push({
      name: index === 0 ? 'Starter' : index === 1 ? 'Pro' : index === 2 ? 'Enterprise' : `Plan ${index + 1}`,
      price: index === 0 ? 'Free' : 'Custom',
      features: [],
      is_popular: index === 1,
    });
  }
  return normalized;
};

const normalizeCategoryPayload = (payload = {}, existing = {}) => ({
  ...existing,
  ...payload,
  name: payload.name || existing.name || '',
  slug: payload.slug || existing.slug || slugify(payload.name || existing.name || ''),
  description: payload.description || existing.description || '',
  meta_title: payload.meta_title || existing.meta_title || '',
  meta_description: payload.meta_description || existing.meta_description || '',
  cover_image: payload.cover_image || existing.cover_image || '',
  background_image: payload.background_image || existing.background_image || '',
  template: payload.template || existing.template || 'default',
});

const normalizeToolPayload = (payload = {}, existing = {}) => {
  const existingAffiliate = existing.affiliate_link || {};
  const payloadAffiliate = payload.affiliate_link || {};
  const referralLink = existingAffiliate.my_referral_link || payloadAffiliate.my_referral_link || '';
  const officialSignup = payloadAffiliate.signup_url || payloadAffiliate.url || existingAffiliate.signup_url || existingAffiliate.url || '';

  const coalesce = (nextValue, currentValue, fallbackValue) => (
    nextValue !== undefined && nextValue !== null ? nextValue : (currentValue !== undefined && currentValue !== null ? currentValue : fallbackValue)
  );

  return {
    ...existing,
    ...payload,
    name: coalesce(payload.name, existing.name, ''),
    slug: coalesce(payload.slug, existing.slug, slugify(payload.name || existing.name || '')),
    tagline: coalesce(payload.tagline, existing.tagline, ''),
    description: coalesce(payload.description, existing.description, ''),
    category_id: coalesce(payload.category_id, existing.category_id, ''),
    logo_url: coalesce(payload.logo_url, existing.logo_url, ''),
    favicon_url: coalesce(payload.favicon_url, existing.favicon_url, ''),
    cta_url: coalesce(payload.cta_url, existing.cta_url, ''),
    website_url: coalesce(payload.website_url, existing.website_url, ''),
    cover_image: coalesce(payload.cover_image, existing.cover_image, ''),
    background_image: coalesce(payload.background_image, existing.background_image, ''),
    gallery_images: Array.isArray(payload.gallery_images) ? payload.gallery_images : (existing.gallery_images || []),
    screenshot_urls: Array.isArray(payload.screenshot_urls) ? payload.screenshot_urls : (existing.screenshot_urls || []),
    video_url: coalesce(payload.video_url, existing.video_url, ''),
    video_embed: coalesce(payload.video_embed, existing.video_embed, ''),
    template: coalesce(payload.template, existing.template, 'default'),
    pricing_tiers: ensureMinimumPricingTiers(payload.pricing_tiers || existing.pricing_tiers || [], 3),
    price_summary: coalesce(payload.price_summary, existing.price_summary, ''),
    monthly_price_estimate: coalesce(payload.monthly_price_estimate, existing.monthly_price_estimate, ''),
    review_count: coalesce(payload.review_count, existing.review_count, 0),
    reviews_count: coalesce(payload.reviews_count, existing.reviews_count, 0),
    ranking_score: coalesce(payload.ranking_score, existing.ranking_score, ''),
    ranking_tier: coalesce(payload.ranking_tier, existing.ranking_tier, ''),
    roi_snapshot: coalesce(payload.roi_snapshot, existing.roi_snapshot, {}),
    review_snapshot: coalesce(payload.review_snapshot, existing.review_snapshot, {}),
    changelog_snapshot: coalesce(payload.changelog_snapshot, existing.changelog_snapshot, {}),
    ranking_snapshot: coalesce(payload.ranking_snapshot, existing.ranking_snapshot, {}),
    showcase_tabs: coalesce(payload.showcase_tabs, existing.showcase_tabs, {}),
    features: ensureArray(payload.features || existing.features || []),
    pros: ensureArray(payload.pros || existing.pros || []),
    cons: ensureArray(payload.cons || existing.cons || []),
    best_for: ensureArray(payload.best_for || existing.best_for || []),
    affiliate_link: {
      ...existingAffiliate,
      ...payloadAffiliate,
      my_referral_link: referralLink,
      signup_url: officialSignup,
      url: referralLink || officialSignup,
      show_commission_public: payloadAffiliate.show_commission_public ?? existingAffiliate.show_commission_public ?? true,
    },
  };
};

const TOOL_FORM_DEFAULTS = {
  name: '', slug: '', tagline: '', description: '', category_id: '',
  website_url: '', affiliate_link: { url: '', network: '', utm_parameters: {}, show_commission_public: true },
  features: [], pros: [], cons: [], best_for: [], pricing_tiers: [],
  cover_image: '', background_image: '', gallery_images: [], video_url: '', video_embed: '', template: 'default',
  price_summary: '', monthly_price_estimate: '', review_count: 0, reviews_count: 0,
  ranking_score: '', ranking_tier: '', roi_snapshot: {}, review_snapshot: {}, changelog_snapshot: {}, ranking_snapshot: {}, showcase_tabs: {}
};

const ADMIN_DASHBOARD_CACHE_KEY = 'wowrolla_admin_dashboard_cache_v1';

const readAdminCache = (key) => {
  try {
    const cached = JSON.parse(localStorage.getItem(key) || 'null');
    return cached && typeof cached === 'object' ? cached : null;
  } catch {
    return null;
  }
};

const writeAdminCache = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore storage errors
  }
};

const AdminDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { adminTheme, toggleAdminTheme } = useTheme();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showToolModal, setShowToolModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTool, setEditingTool] = useState(null);
  const [refreshingToolIds, setRefreshingToolIds] = useState([]);
  const [consultingBadgeCount, setConsultingBadgeCount] = useState(0);
  const [errorModal, setErrorModal] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }

    const cached = readAdminCache(ADMIN_DASHBOARD_CACHE_KEY);
    if (cached?.categories || cached?.tools) {
      setCategories(Array.isArray(cached.categories) ? cached.categories : []);
      setTools(Array.isArray(cached.tools) ? cached.tools : []);
      setLoading(false);
    }

    loadData();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const loadConsultingBadge = async () => {
      try {
        const key = localStorage.getItem('wowrolla_admin_key') || '';
        if (!key) return;
        const [prospectRes, pilotRes] = await Promise.all([
          consultingApi.getClients(key, { stage: 'prospect' }),
          consultingApi.getClients(key, { stage: 'pilot' }),
        ]);
        const nextCount = (prospectRes.data || []).length + (pilotRes.data || []).length;
        setConsultingBadgeCount(nextCount);
      } catch {
        setConsultingBadgeCount(0);
      }
    };

    loadConsultingBadge();
  }, []);

  const loadData = async () => {
    try {
      const [catRes, toolRes] = await Promise.all([
        adminApi.getCategories(),
        adminApi.getTools()
      ]);
      const nextCategories = Array.isArray(catRes.data) ? catRes.data : [];
      const nextTools = Array.isArray(toolRes.data) ? toolRes.data : [];
      setCategories(nextCategories);
      setTools(nextTools);
      writeAdminCache(ADMIN_DASHBOARD_CACHE_KEY, { categories: nextCategories, tools: nextTools, updatedAt: new Date().toISOString() });
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await adminApi.deleteCategory(id);
        setCategories(categories.filter(c => c.id !== id));
      } catch (error) {
        alert('Failed to delete category');
      }
    }
  };

  const handleDeleteTool = async (id) => {
    if (window.confirm('Are you sure you want to delete this tool?')) {
      try {
        await adminApi.deleteTool(id);
        setTools(tools.filter(t => t.id !== id));
      } catch (error) {
        alert('Failed to delete tool');
      }
    }
  };

  const handleRefreshToolFromSource = async (tool) => {
    if (!tool?.id) return;
    if (!window.confirm(`Update ${tool.name} from its official website now? This keeps existing valid values if source data is missing or invalid.`)) {
      return;
    }

    setRefreshingToolIds((prev) => (prev.includes(tool.id) ? prev : [...prev, tool.id]));
    try {
      const response = await adminApi.refreshToolFromSource(tool.id, {
        referral_link: tool?.affiliate_link?.my_referral_link || undefined,
        allow_category_change: false,
        minimum_confidence: 0.8,
      });
      const updated = response?.data;
      if (updated?.id) {
        setTools((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      }
      const policy = updated?.source_update_policy || {};
      if (policy?.status === 'no_change_scrape_unavailable') {
        setErrorModal({
          title: 'Update: No Changes (Scraper Unavailable)',
          message: 'Source website blocked automated scraping. Existing tool data was preserved without changes.',
          toolName: tool.name,
          type: 'info',
        });
      } else {
        setErrorModal({
          title: 'Update Successful',
          message: `${tool.name} was successfully updated from the official website.`,
          toolName: tool.name,
          type: 'success',
        });
      }
    } catch (error) {
      const backendDetail = error?.response?.data?.detail || '';
      const errorMessage = backendDetail || 'Tool update failed. Existing data was kept as-is.';
      setErrorModal({
        title: 'Tool Update Failed',
        message: errorMessage,
        toolName: tool.name,
        type: 'error',
        statusCode: error?.response?.status,
      });
    } finally {
      setRefreshingToolIds((prev) => prev.filter((id) => id !== tool.id));
    }
  };

  const handleImportToolJson = async (file) => {
    if (!file) return;

    const normalizeBackendBase = (value) =>
      String(value || '')
        .trim()
        .replace(/\/$/, '')
        .replace(/\/api$/, '');

    const backendBases = [...new Set([
      normalizeBackendBase(process.env.REACT_APP_BACKEND_URL),
      normalizeBackendBase(process.env.REACT_APP_API_BASE),
      '/api',
      'http://127.0.0.1:8001',
    ].filter(Boolean))];

    const token = localStorage.getItem('authToken') || localStorage.getItem('admin_token');
    let lastError = null;

    for (const base of backendBases) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('auto_create_categories', 'true');

        const response = await fetch(`${base}/api/admin/tools/import/json`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });

        const responseText = await response.text();
        let parsed = null;
        try {
          parsed = responseText ? JSON.parse(responseText) : null;
        } catch {
          parsed = null;
        }

        if (!response.ok) {
          const detail = parsed?.detail || responseText || `HTTP ${response.status}`;
          const nextError = new Error(String(detail));
          nextError.response = { status: response.status, data: parsed || responseText };
          lastError = nextError;

          if (response.status !== 404) {
            throw nextError;
          }
          continue;
        }

        const result = parsed || {};
        const importedTool = result.tool;
        const exists = tools.some((t) => t.id === importedTool.id);
        if (exists) {
          setTools(tools.map((t) => (t.id === importedTool.id ? importedTool : t)));
        } else {
          setTools([...tools, importedTool]);
        }
        alert(result.message || 'Tool imported successfully');
        return;
      } catch (error) {
        lastError = error;
      }
    }

    alert('Failed to import tool: ' + (lastError?.message || 'Unknown error'));
  };

  const handleBulkImportToolJson = async (files) => {
    if (!files || files.length === 0) return;

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('files', file));
    formData.append('auto_create_categories', 'true');
    formData.append('continue_on_error', 'true');

    const normalizeBackendBase = (value) =>
      String(value || '')
        .trim()
        .replace(/\/$/, '')
        .replace(/\/api$/, '');

    const backendBases = [...new Set([
      normalizeBackendBase(process.env.REACT_APP_BACKEND_URL),
      normalizeBackendBase(process.env.REACT_APP_API_BASE),
      '/api',
      'http://127.0.0.1:8001',
    ].filter(Boolean))];

    const token = localStorage.getItem('authToken') || localStorage.getItem('admin_token');

    const tryBulkImportViaFetch = async () => {
      let lastError = null;

      for (const base of backendBases) {
        try {
          const requestForm = new FormData();
          Array.from(files).forEach((file) => requestForm.append('files', file));
          requestForm.append('auto_create_categories', 'true');
          requestForm.append('continue_on_error', 'true');

          const response = await fetch(`${base}/api/admin/tools/import/json/bulk`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: requestForm,
          });

          const responseText = await response.text();
          let parsed = null;
          try {
            parsed = responseText ? JSON.parse(responseText) : null;
          } catch {
            parsed = null;
          }

          if (!response.ok) {
            const detail = parsed?.detail || responseText || `HTTP ${response.status}`;
            const nextError = new Error(String(detail));
            nextError.response = { status: response.status, data: parsed || responseText };
            lastError = nextError;
            if (response.status !== 404) throw nextError;
            continue;
          }

          return parsed || {};
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error('Bulk import endpoint not reachable');
    };

    try {
      let payload = null;

      try {
        const response = await adminApi.importToolsJsonBulk(formData);
        payload = response?.data || null;
      } catch (error) {
        if (error?.response?.status !== 404) {
          throw error;
        }
        payload = await tryBulkImportViaFetch();
      }

      const imported = Array.isArray(payload?.tools) ? payload.tools : [];

      if (imported.length) {
        setTools((prev) => {
          const map = new Map(prev.map((tool) => [tool.id, tool]));
          imported.forEach((tool) => {
            if (tool?.id) map.set(tool.id, tool);
          });
          return Array.from(map.values());
        });
      }

      const summary = payload?.summary || {};
      alert(
        `Bulk import complete. Created: ${summary.created || 0}, Updated: ${summary.updated || 0}, Failed: ${summary.failed || 0}, Categories created: ${summary.categories_created || 0}`
      );
    } catch (error) {
      alert('Bulk import failed: ' + (error?.response?.data?.detail || error?.message || 'Unknown error'));
    }
  };

  const handleExportToolJson = async (tool) => {
    try {
      const response = await adminApi.exportToolJson(tool.id);
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = (
        response.headers['content-disposition']?.match(/filename="([^"]+)"/)?.[1]
      ) || `${tool.slug || tool.id}-export.json`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback to client-side export with all locally-available fields
      const exportData = {
        id: tool.id,
        name: tool.name,
        slug: tool.slug,
        tagline: tool.tagline,
        description: tool.description,
        category_id: tool.category_id,
        category_name: tool.category_name,
        website_url: tool.website_url,
        logo_url: tool.logo_url,
        favicon_url: tool.favicon_url,
        cover_image: tool.cover_image,
        background_image: tool.background_image,
        gallery_images: tool.gallery_images || [],
        screenshot_urls: tool.screenshot_urls || [],
        video_url: tool.video_url,
        video_embed: tool.video_embed,
        pricing_tiers: tool.pricing_tiers || [],
        price_summary: tool.price_summary,
        monthly_price_estimate: tool.monthly_price_estimate,
        features: tool.features || [],
        best_for: tool.best_for || [],
        pros: tool.pros || [],
        cons: tool.cons || [],
        pain_points: tool.pain_points || [],
        time_saving_rate: tool.time_saving_rate,
        competitor_adoption_pct: tool.competitor_adoption_pct,
        implementation_complexity: tool.implementation_complexity,
        priming_signals: tool.priming_signals || {},
        rating: tool.rating || {},
        review_count: tool.review_count,
        review_snapshot: tool.review_snapshot || {},
        roi_snapshot: tool.roi_snapshot || {},
        ranking_score: tool.ranking_score,
        ranking_tier: tool.ranking_tier,
        ranking_snapshot: tool.ranking_snapshot || {},
        showcase_tabs: tool.showcase_tabs || {},
        content_blocks: tool.content_blocks || [],
        affiliate_link: tool.affiliate_link || {},
        cta_url: tool.cta_url,
        meta_title: tool.meta_title,
        meta_description: tool.meta_description,
        is_featured: tool.is_featured,
        last_source_json: tool.last_source_json || {},
        last_source_json_file_name: tool.last_source_json_file_name,
        created_at: tool.created_at,
        updated_at: tool.updated_at,
      };
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tool.slug || tool.id}-export.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadSourceJson = (tool) => {
    const payload = tool?.last_source_json;
    if (!payload || Object.keys(payload).length === 0) {
      alert('No source JSON snapshot available for this tool yet.');
      return;
    }
    const fileName = tool?.last_source_json_file_name || `${tool?.slug || 'tool'}-source.json`;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center text-text-secondary">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-yellow"></div>
          <p className="mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  const navSections = [
    {
      heading: 'Start Here',
      items: [
        { id: 'dashboard', label: 'Overview Dashboard', icon: LayoutDashboard },
      ],
    },
    {
      heading: 'Manage Catalog',
      items: [
        { id: 'categories', label: 'Categories', icon: FolderTree },
        { id: 'tools', label: 'Tools', icon: Wrench },
      ],
    },
    {
      heading: 'Growth Content',
      items: [
        { id: 'content-gen', label: 'Content Generator', icon: Sparkles },
        { id: 'blog', label: 'Blog', icon: FileText },
        { id: 'subscribers', label: 'Subscribers', icon: Users },
      ],
    },
    {
      heading: 'Revenue & Ops',
      items: [
        { id: 'commissions', label: 'Commissions', icon: DollarSign },
        { id: 'aeo-admin', label: 'AEO Dashboard', icon: SearchCode },
        { id: 'settings', label: 'Settings', icon: Settings },
      ],
    },
  ];

  const finderTabItems = [
    { id: 'finder-analytics', label: 'Insights', icon: SearchCode },
    { id: 'comparison-config-admin', label: 'Comparison Rules', icon: BarChart3 },
    { id: 'fit-scores-admin', label: 'Fit Scores', icon: BarChart3 },
    { id: 'reviews-admin', label: 'Reviews', icon: MessageSquareQuote },
    { id: 'newsletter-admin', label: 'Newsletter', icon: Mail },
    { id: 'changelogs-admin', label: 'Changelogs', icon: FileClock },
    { id: 'seo-pages-admin', label: 'SEO Pages', icon: FileSearch2 },
    { id: 'seo-settings-admin', label: 'SEO Settings', icon: SlidersHorizontal },
    { id: 'faq-admin', label: 'FAQ Generator', icon: MessageSquareQuote },
    { id: 'pricing-monitor-admin', label: 'Pricing Monitor', icon: LineChart },
    { id: 'health-monitor-admin', label: 'Health Monitor', icon: ActivitySquare },
    { id: 'logging-admin', label: 'Logging Center', icon: ShieldCheck },
    { id: 'sponsorships-admin', label: 'Sponsorships', icon: Handshake },
    { id: 'guides-admin', label: 'Guides', icon: BookOpen },
    { id: 'partners-admin', label: 'Partners', icon: UsersRound },
  ];

  return (
    <div className="min-h-screen bg-void admin-theme-shell" data-admin-theme={adminTheme} data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-surface border-b border-white/10 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-black text-cyber-yellow" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                WOWROLLA ADMIN
              </h1>
              <Link to="/" className="text-sm text-text-secondary hover:text-cyber-yellow transition-colors">
                View Site
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-text-secondary text-sm">Welcome, {user?.full_name}</span>
              <button onClick={toggleAdminTheme} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="admin-theme-toggle-button">
                Admin {adminTheme === 'light' ? 'Light' : 'Dark'}
              </button>
              <button onClick={handleLogout} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="logout-button">
                <LogOut size={16} /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-surface border-r border-white/10 min-h-screen sticky top-16 flex-shrink-0">
          <nav className="p-4">
            {navSections.map((section) => (
              <div key={section.heading} className="mb-4">
                <p className="px-4 py-1 text-[10px] uppercase tracking-widest text-text-muted">{section.heading}</p>
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full text-left px-4 py-3 mb-1 flex items-center gap-3 transition-colors rounded ${
                      activeTab === item.id
                        ? 'bg-cyber-yellow/10 text-cyber-yellow'
                        : 'text-text-secondary hover:text-white hover:bg-white/5'
                    }`}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon size={18} />
                    <span className="text-sm">{item.label}</span>
                  </button>
                ))}
              </div>
            ))}

            <Link
              to="/admin/consulting"
              className="w-full text-left px-4 py-3 mb-1 flex items-center justify-between transition-colors rounded text-text-secondary hover:text-cyber-yellow hover:bg-cyber-yellow/10 border border-white/10"
              data-testid="nav-consulting-hub"
            >
              <span className="inline-flex items-center gap-3">
                <BriefcaseBusiness size={18} />
                <span className="text-sm">Consulting Hub</span>
              </span>
              {consultingBadgeCount > 0 && (
                <span className="min-w-5 h-5 px-1 inline-flex items-center justify-center bg-cyber-yellow text-black text-xs font-bold rounded-full">
                  {consultingBadgeCount}
                </span>
              )}
            </Link>

            <Link
              to="/admin/users"
              className="w-full text-left px-4 py-3 mb-1 flex items-center gap-3 transition-colors rounded text-text-secondary hover:text-cyber-yellow hover:bg-cyber-yellow/10 border border-white/10"
              data-testid="nav-users"
            >
              <Users size={18} />
              <span className="text-sm">Users</span>
            </Link>

            <Link
              to="/admin/sales"
              className="w-full text-left px-4 py-3 mb-1 flex items-center gap-3 transition-colors rounded text-text-secondary hover:text-cyber-yellow hover:bg-cyber-yellow/10 border border-white/10"
              data-testid="nav-sales-management"
            >
              <TrendingUp size={18} />
              <span className="text-sm">Sales Management</span>
            </Link>

            <Link
              to="/admin/command-center"
              className="w-full text-left px-4 py-3 mb-1 flex items-center gap-3 transition-colors rounded text-text-secondary hover:text-cyber-yellow hover:bg-cyber-yellow/10 border border-white/10"
              data-testid="nav-command-center"
            >
              <ShieldCheck size={18} />
              <span className="text-sm">Command Center</span>
            </Link>

            <Link
              to="/marketing"
              className="w-full text-left px-4 py-3 mt-4 mb-1 flex items-center gap-3 transition-colors rounded text-text-secondary hover:text-cyber-yellow hover:bg-cyber-yellow/10 border border-white/10"
              data-testid="nav-marketing-page"
            >
              <Megaphone size={18} />
              <span className="text-sm">Marketing</span>
            </Link>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 min-w-0">
          <div className="terminal-grid p-3 mb-6" data-testid="admin-growth-tab-row">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {finderTabItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className="px-4 py-2 whitespace-nowrap text-sm font-semiemibold inline-flex items-center gap-2 transition-colors"
                  style={{
                    background: activeTab === item.id ? 'var(--accent-primary)' : 'transparent',
                    color: activeTab === item.id ? '#000' : 'var(--text-secondary)',
                    border: `1px solid ${activeTab === item.id ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                  }}
                  data-testid={`finder-tab-${item.id}`}
                >
                  <item.icon size={15} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'dashboard' && <DashboardOverview />}

          {activeTab === 'categories' && (
            <CategoriesTab
              categories={categories}
              onAdd={() => { setEditingCategory(null); setShowCategoryModal(true); }}
              onEdit={(cat) => { setEditingCategory(cat); setShowCategoryModal(true); }}
              onDelete={handleDeleteCategory}
            />
          )}

          {activeTab === 'tools' && (
            <ToolsTab
              tools={tools}
              onAdd={() => { setEditingTool(null); setShowToolModal(true); }}
              onEdit={(tool) => { setEditingTool(tool); setShowToolModal(true); }}
              onRefreshFromSource={handleRefreshToolFromSource}
              onDelete={handleDeleteTool}
              onImportJson={handleImportToolJson}
              onBulkImportJson={handleBulkImportToolJson}
              onExportJson={handleExportToolJson}
              onDownloadSourceJson={handleDownloadSourceJson}
              refreshingToolIds={refreshingToolIds}
            />
          )}

          {activeTab === 'blog' && <BlogManager />}
          {activeTab === 'commissions' && <CommissionsManager />}
          {activeTab === 'subscribers' && <SubscribersManager />}
          {activeTab === 'content-gen' && <ContentGenerator />}
          {activeTab === 'aeo-admin' && <AEODashboard />}
          {activeTab === 'settings' && <SiteSettingsPage />}
          {activeTab === 'finder-analytics' && <FinderAnalyticsTab />}
          {activeTab === 'comparison-config-admin' && <ComparisonConfigTab tools={tools} />}
          {activeTab === 'reviews-admin' && <ReviewsAdminTab tools={tools} />}
          {activeTab === 'newsletter-admin' && <NewsletterAdminTab />}
          {activeTab === 'changelogs-admin' && <ChangelogsAdminTab tools={tools} />}
          {activeTab === 'seo-pages-admin' && <SeoPagesAdminTab />}
          {activeTab === 'faq-admin' && <FAQAdminPage />}
          {activeTab === 'fit-scores-admin' && <FitScoresAdminTab />}
          {activeTab === 'pricing-monitor-admin' && <PricingMonitorAdminTab />}
          {activeTab === 'health-monitor-admin' && <HealthMonitorAdminTab />}
          {activeTab === 'logging-admin' && <LoggingAdminTab />}
          {activeTab === 'sponsorships-admin' && <SponsorshipsAdminTab />}
          {activeTab === 'guides-admin' && <GuidesAdminTab />}
          {activeTab === 'partners-admin' && <PartnersAdminTab />}
          {activeTab === 'seo-settings-admin' && <SeoSettingsAdminTab />}
        </main>
      </div>

      {showCategoryModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setShowCategoryModal(false)}
          onSave={(newCategory) => {
            if (editingCategory) {
              setCategories(categories.map(c => c.id === newCategory.id ? newCategory : c));
            } else {
              setCategories([...categories, newCategory]);
            }
            setShowCategoryModal(false);
          }}
        />
      )}

      {showToolModal && (
        <ToolModal
          tool={editingTool}
          categories={categories}
          onClose={() => setShowToolModal(false)}
          onSave={(newTool) => {
            const exists = tools.some((t) => t.id === newTool.id);
            if (editingTool || exists) {
              setTools(tools.map(t => t.id === newTool.id ? newTool : t));
            } else {
              setTools([...tools, newTool]);
            }
            setShowToolModal(false);
          }}
        />
      )}
    </div>
  );
};

// Categories Tab
const CategoriesTab = ({ categories, onAdd, onEdit, onDelete }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
    <div className="flex items-center justify-between mb-8">
      <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Categories</h2>
      <button onClick={onAdd} className="btn-primary inline-flex items-center gap-2" data-testid="add-category-button">
        <Plus size={20} /> Add / Import Category
      </button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {categories.map(category => (
        <div key={category.id} className="terminal-grid p-6" data-testid={`category-item-${category.slug}`}>
          <h3 className="text-lg font-semibold text-white mb-2">{category.name}</h3>
          <p className="text-sm text-text-secondary mb-3 line-clamp-2">{category.description}</p>
          <div className="text-xs text-text-muted font-mono mb-4">{category.tool_count || 0} tools</div>
          <div className="flex gap-2">
            <button onClick={() => onEdit(category)} className="btn-secondary text-sm flex items-center gap-1"><Edit size={14} /> Edit</button>
            <button onClick={() => onDelete(category.id)} className="text-red-500 hover:text-red-400 text-sm flex items-center gap-1"><Trash2 size={14} /> Delete</button>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

const FinderAnalyticsTab = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({ weekly_logs: [], top_queries: [], settings: null });
  const [saving, setSaving] = useState(false);
  const [fitWeight, setFitWeight] = useState(0.8);
  const [commissionWeight, setCommissionWeight] = useState(0.2);
  const [includedCategories, setIncludedCategories] = useState([]);
  const [aiProvider, setAiProvider] = useState('anthropic');
  const [aiModel, setAiModel] = useState('claude-sonnet-4-20250514');
  const [allCategories, setAllCategories] = useState([]);

  const loadFinderAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, categoriesRes] = await Promise.all([
        adminApi.getFinderAnalytics(),
        adminApi.getCategories(),
      ]);
      const analytics = analyticsRes.data || {};
      const settings = analytics.settings || { fit_weight: 0.8, commission_weight: 0.2, included_category_ids: [] };
      setDashboard(analytics);
      setAllCategories(categoriesRes.data || []);
      setFitWeight(settings.fit_weight ?? 0.8);
      setCommissionWeight(settings.commission_weight ?? 0.2);
      setIncludedCategories(settings.included_category_ids || []);
      setAiProvider(settings.ai_provider || 'anthropic');
      setAiModel(settings.ai_model || 'claude-sonnet-4-20250514');
    } catch (error) {
      alert('Failed to load finder analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFinderAnalytics();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await adminApi.updateFinderSettings({
        fit_weight: Number(fitWeight),
        commission_weight: Number(commissionWeight),
        included_category_ids: includedCategories,
        ai_provider: aiProvider,
        ai_model: aiModel,
      });
      await loadFinderAnalytics();
    } catch (error) {
      alert('Failed to save finder settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setIncludedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  if (loading) {
    return (
      <div className="terminal-grid p-8" data-testid="finder-analytics-loading">
        <p className="text-text-secondary">Loading finder analytics...</p>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid="finder-analytics-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="terminal-grid p-6">
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>Finder Logs (Weekly)</h2>
          {dashboard.weekly_logs?.length ? (
            <div className="space-y-2">
              {dashboard.weekly_logs.map((week) => (
                <div key={week.week} className="flex items-center justify-between p-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                  <span className="text-sm text-text-secondary">Week of {week.week}</span>
                  <span className="text-sm font-mono" style={{ color: 'var(--accent-primary)' }}>{week.count} sessions</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-text-secondary">No finder sessions logged yet.</p>
          )}
        </div>

        <div className="terminal-grid p-6">
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>Top 10 Queries</h2>
          {dashboard.top_queries?.length ? (
            <div className="space-y-2">
              {dashboard.top_queries.map((query, idx) => (
                <div key={`${query.query}-${idx}`} className="flex items-center justify-between p-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                  <span className="text-sm text-text-secondary truncate pr-3">{query.query}</span>
                  <span className="text-sm font-mono" style={{ color: 'var(--accent-secondary)' }}>{query.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-text-secondary">No query trends available yet.</p>
          )}
        </div>
      </div>

      <div className="terminal-grid p-6">
        <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>Recommendation Weighting</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <label className="text-sm text-text-secondary">
            Fit Weight
            <input
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={fitWeight}
              onChange={(e) => setFitWeight(e.target.value)}
              className="w-full mt-2 px-3 py-2 bg-transparent border border-white/20 text-white"
              data-testid="finder-fit-weight"
            />
          </label>
          <label className="text-sm text-text-secondary">
            Commission Weight
            <input
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={commissionWeight}
              onChange={(e) => setCommissionWeight(e.target.value)}
              className="w-full mt-2 px-3 py-2 bg-transparent border border-white/20 text-white"
              data-testid="finder-commission-weight"
            />
          </label>
          <label className="text-sm text-text-secondary">
            AI Provider
            <select
              value={aiProvider}
              onChange={(e) => {
                const provider = e.target.value;
                setAiProvider(provider);
                if (provider === 'anthropic' && !aiModel.toLowerCase().includes('claude')) {
                  setAiModel('claude-sonnet-4-20250514');
                }
                if (provider === 'openai' && !aiModel.toLowerCase().includes('gpt')) {
                  setAiModel('gpt-4o-mini');
                }
              }}
              className="w-full mt-2 px-3 py-2 bg-transparent border border-white/20 text-white"
              data-testid="finder-ai-provider"
            >
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
            </select>
          </label>
          <label className="text-sm text-text-secondary">
            AI Model
            <input
              type="text"
              value={aiModel}
              onChange={(e) => setAiModel(e.target.value)}
              className="w-full mt-2 px-3 py-2 bg-transparent border border-white/20 text-white"
              placeholder={aiProvider === 'anthropic' ? 'claude-sonnet-4-20250514' : 'gpt-4o-mini'}
              data-testid="finder-ai-model"
            />
          </label>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">Included Categories</h4>
        <div className="flex flex-wrap gap-2 mb-6">
          {allCategories.map((category) => {
            const selected = includedCategories.includes(category.id);
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => toggleCategory(category.id)}
                className="px-3 py-2 text-sm"
                style={{
                  background: selected ? 'var(--accent-primary)' : 'transparent',
                  color: selected ? '#000' : 'var(--text-secondary)',
                  border: `1px solid ${selected ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                }}
              >
                {category.name}
              </button>
            );
          })}
        </div>

        <button onClick={handleSaveSettings} disabled={saving} className="btn-primary" data-testid="save-finder-settings">
          {saving ? 'Saving...' : 'Save Finder Settings'}
        </button>
      </div>
    </motion.div>
  );
};

const ComparisonConfigTab = ({ tools }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dimensionsInput, setDimensionsInput] = useState('overall_fit, ease_of_use, feature_depth, value_for_money, integrations, support');
  const [excludedToolIds, setExcludedToolIds] = useState([]);
  const [topPairs, setTopPairs] = useState([]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const [configRes, pairsRes] = await Promise.all([
        adminApi.getComparisonConfig(),
        adminApi.getTopComparisonPairs(),
      ]);
      const config = configRes.data || {};
      setDimensionsInput((config.dimensions || []).join(', '));
      setExcludedToolIds(config.excluded_tool_ids || []);
      setTopPairs(pairsRes.data?.items || []);
    } catch (error) {
      alert('Failed to load comparison config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const toggleExcludedTool = (toolId) => {
    setExcludedToolIds((prev) =>
      prev.includes(toolId)
        ? prev.filter((id) => id !== toolId)
        : [...prev, toolId]
    );
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const dimensions = dimensionsInput
        .split(',')
        .map((item) => item.trim().toLowerCase().replace(/\s+/g, '_'))
        .filter(Boolean);
      await adminApi.updateComparisonConfig({ dimensions, excluded_tool_ids: excludedToolIds });
      await loadConfig();
    } catch (error) {
      alert('Failed to save comparison config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="terminal-grid p-6" data-testid="comparison-config-loading"><p className="text-text-secondary">Loading comparison config...</p></div>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid="comparison-config-tab">
      <div className="terminal-grid p-6 mb-6">
        <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>Comparison Config</h2>
        <label className="block text-sm text-text-secondary mb-2">Dimensions to show in comparison tables (comma separated)</label>
        <input
          value={dimensionsInput}
          onChange={(e) => setDimensionsInput(e.target.value)}
          className="w-full px-3 py-2 bg-transparent border border-white/20 text-white mb-4"
          data-testid="comparison-dimensions-input"
        />

        <h3 className="text-lg font-semibold text-white mb-3">Exclude Tools from Comparison</h3>
        <div className="max-h-56 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          {tools.map((tool) => {
            const selected = excludedToolIds.includes(tool.id);
            return (
              <button
                key={tool.id}
                type="button"
                onClick={() => toggleExcludedTool(tool.id)}
                className="px-3 py-2 text-sm text-left"
                style={{
                  border: `1px solid ${selected ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                  background: selected ? 'rgba(255,232,0,0.12)' : 'transparent',
                  color: selected ? 'var(--accent-primary)' : 'var(--text-secondary)',
                }}
              >
                {tool.name}
              </button>
            );
          })}
        </div>

        <button onClick={saveConfig} disabled={saving} className="btn-primary" data-testid="save-comparison-config">
          {saving ? 'Saving...' : 'Save Comparison Config'}
        </button>
      </div>

      <div className="terminal-grid p-6">
        <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>Most Compared Tool Pairs This Month</h3>
        {topPairs.length === 0 ? (
          <p className="text-text-secondary">No comparison pair events yet for this month.</p>
        ) : (
          <div className="space-y-2">
            {topPairs.map((pair) => (
              <div key={pair.pair_key} className="flex items-center justify-between p-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{(pair.tool_names || []).join(' vs ')}</span>
                <span className="text-sm font-mono" style={{ color: 'var(--accent-primary)' }}>{pair.count} comparisons</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const ReviewsAdminTab = ({ tools }) => {
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ min_word_count: 50, auto_summarize: true, auto_weekly_sync: true, default_sync_interval_days: 7 });
  const [importContent, setImportContent] = useState('');
  const [sourceLabel, setSourceLabel] = useState('G2 Import');
  const [importFormat, setImportFormat] = useState('csv');
  const [defaultToolId, setDefaultToolId] = useState('');
  const [syncSources, setSyncSources] = useState([]);
  const [sourceForm, setSourceForm] = useState({
    id: null,
    name: '',
    provider: 'g2',
    source_url: '',
    source_format: 'csv',
    source_label: 'G2 Import',
    review_type: 'tool',
    tool_id: '',
    tool_slug: '',
    auto_sync_enabled: true,
    sync_interval_days: 7,
  });
  const [importing, setImporting] = useState(false);
  const [syncingSourceId, setSyncingSourceId] = useState('');
  const uploadInputRef = React.useRef(null);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const [reviewsRes, settingsRes, sourcesRes] = await Promise.all([
        adminApi.getReviewsModeration(statusFilter),
        adminApi.getReviewSettings(),
        adminApi.getReviewSyncSources(),
      ]);
      setReviews(reviewsRes.data?.items || []);
      setSettings(settingsRes.data || { min_word_count: 50, auto_summarize: true, auto_weekly_sync: true, default_sync_interval_days: 7 });
      setSyncSources(sourcesRes.data?.items || []);
    } catch (error) {
      alert('Failed to load reviews admin data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const moderate = async (reviewId, action) => {
    try {
      await adminApi.moderateReview(reviewId, action);
      await loadReviews();
    } catch (error) {
      alert('Moderation action failed');
    }
  };

  const saveReviewSettings = async () => {
    try {
      await adminApi.updateReviewSettings(settings);
      alert('Review settings saved');
    } catch (error) {
      alert('Failed to save review settings');
    }
  };

  const runToolSummary = async (toolId) => {
    try {
      await adminApi.summarizeToolReviews(toolId);
      alert('AI summary generated');
    } catch (error) {
      alert('Failed to generate summary');
    }
  };

  const importTypedContent = async () => {
    if (!importContent.trim()) {
      alert(`Paste ${importFormat.toUpperCase()} content first.`);
      return;
    }
    setImporting(true);
    try {
      const request = importFormat === 'json'
        ? adminApi.importReviewsJson(importContent, sourceLabel, '', defaultToolId)
        : adminApi.importReviewsCsv(importContent, sourceLabel, '', defaultToolId);
      const response = await request;
      alert(`Imported ${response.data?.inserted || 0} reviews. Duplicates skipped: ${response.data?.duplicates || 0}.`);
      setImportContent('');
      await loadReviews();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      alert(detail || `${importFormat.toUpperCase()} import failed.`);
    } finally {
      setImporting(false);
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const response = await adminApi.importReviewsFile(file, { sourceLabel, sourceFormat: importFormat, defaultToolId });
      alert(`Imported ${response.data?.inserted || 0} reviews from file. Duplicates skipped: ${response.data?.duplicates || 0}.`);
      await loadReviews();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Review file import failed');
    } finally {
      setImporting(false);
      if (uploadInputRef.current) {
        uploadInputRef.current.value = '';
      }
    }
  };

  const saveSyncSource = async () => {
    if (!sourceForm.name.trim() || !sourceForm.source_url.trim()) {
      alert('Provide a source name and a public CSV/JSON URL.');
      return;
    }
    try {
      await adminApi.saveReviewSyncSource({
        ...sourceForm,
        tool_id: sourceForm.tool_id || null,
        tool_slug: null,
      });
      setSourceForm({
        id: null,
        name: '',
        provider: 'g2',
        source_url: '',
        source_format: 'csv',
        source_label: 'G2 Import',
        review_type: 'tool',
        tool_id: '',
        tool_slug: '',
        auto_sync_enabled: true,
        sync_interval_days: settings.default_sync_interval_days || 7,
      });
      await loadReviews();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to save review sync source');
    }
  };

  const runSourceSync = async (sourceId) => {
    setSyncingSourceId(sourceId);
    try {
      const response = await adminApi.runReviewSyncSource(sourceId);
      alert(`Sync imported ${response.data?.inserted || 0} reviews. Duplicates skipped: ${response.data?.duplicates || 0}.`);
      await loadReviews();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Remote sync failed');
    } finally {
      setSyncingSourceId('');
    }
  };

  const editSource = (source) => {
    setSourceForm({
      id: source.id,
      name: source.name || '',
      provider: source.provider || 'custom',
      source_url: source.source_url || '',
      source_format: source.source_format || 'csv',
      source_label: source.source_label || 'Imported',
      review_type: source.review_type || 'tool',
      tool_id: source.tool_id || '',
      tool_slug: source.tool_slug || '',
      auto_sync_enabled: !!source.auto_sync_enabled,
      sync_interval_days: source.sync_interval_days || 7,
    });
  };

  const deleteSource = async (sourceId) => {
    try {
      await adminApi.deleteReviewSyncSource(sourceId);
      await loadReviews();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to delete review sync source');
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid="reviews-admin-tab">
      <div className="terminal-grid p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Reviews Moderation</h2>
          <InfoTag label="Bulk import with deduplication" description="Import and moderate reviews from CSV/JSON with automatic duplicate detection and tool assignment" variant="primary" />
        </div>
        <div className="flex flex-wrap gap-3 mb-4 items-end">
          <label className="text-sm text-text-secondary">
            Queue
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="ml-2 px-3 py-2 bg-surface border border-white/20 text-white">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="flagged">Flagged</option>
              <option value="all">All</option>
            </select>
          </label>
          <label className="text-sm text-text-secondary">
            Min words
            <input
              type="number"
              min="20"
              max="500"
              value={settings.min_word_count}
              onChange={(e) => setSettings({ ...settings, min_word_count: Number(e.target.value) || 50 })}
              className="input-pro ml-2 w-32"
            />
          </label>
          <label className="text-sm text-text-secondary inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!settings.auto_summarize}
              onChange={(e) => setSettings({ ...settings, auto_summarize: e.target.checked })}
            />
            Auto summarise
          </label>
          <label className="text-sm text-text-secondary inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!settings.auto_weekly_sync}
              onChange={(e) => setSettings({ ...settings, auto_weekly_sync: e.target.checked })}
            />
            Allow weekly remote sync
          </label>
          <label className="text-sm text-text-secondary">
            Sync every
            <input
              type="number"
              min="1"
              max="30"
              value={settings.default_sync_interval_days || 7}
              onChange={(e) => setSettings({ ...settings, default_sync_interval_days: Number(e.target.value) || 7 })}
              className="input-pro ml-2 inline-flex w-24"
            />
            <span className="ml-2">days</span>
          </label>
          <button onClick={saveReviewSettings} className="btn-primary">Save Review Settings</button>
        </div>

        {loading ? (
          <p className="text-text-secondary">Loading moderation queue...</p>
        ) : reviews.length === 0 ? (
          <p className="text-text-secondary">No reviews in this queue.</p>
        ) : (
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {reviews.map((review) => (
              <div key={review.id} className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-semibold text-white">{review.tool_name} - {review.author_name || 'Anonymous'}</div>
                    <div className="text-xs text-text-muted">{review.rating}/5 - {review.role_profile || 'role n/a'} - {review.company_size || 'size n/a'} - {review.source_label || 'Community'}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => moderate(review.id, 'approved')} className="btn-primary text-xs px-3 py-1">Approve</button>
                    <button onClick={() => moderate(review.id, 'rejected')} className="btn-secondary text-xs px-3 py-1">Reject</button>
                    <button onClick={() => moderate(review.id, 'flagged')} className="btn-secondary text-xs px-3 py-1">Flag</button>
                  </div>
                </div>
                {review.title && <h4 className="font-semibold text-white mb-1">{review.title}</h4>}
                <p className="text-sm text-text-secondary">{review.body}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="terminal-grid p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
          <div>
            <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Bulk Import Reviews</h3>
            <p className="text-sm mt-1 text-text-secondary">Import pasted content or local CSV/JSON files. Weekly sync sources appear in the next panel.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={importTypedContent} disabled={importing} className="btn-primary">{importing ? 'Importing...' : `Import ${importFormat.toUpperCase()}`}</button>
            <button type="button" onClick={() => uploadInputRef.current?.click()} disabled={importing} className="btn-secondary">Upload File</button>
            <input
              ref={uploadInputRef}
              type="file"
              accept=".csv,.json"
              className="hidden"
              onChange={(e) => importFile(e.target.files?.[0])}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <label className="text-sm text-text-secondary">
            Source label
            <input value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)} className="input-pro mt-2" placeholder="Source label" />
          </label>
          <label className="text-sm text-text-secondary">
            Format
            <select value={importFormat} onChange={(e) => setImportFormat(e.target.value)} className="input-pro mt-2">
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
          </label>
          <label className="text-sm text-text-secondary">
            Default tool override
            <select value={defaultToolId} onChange={(e) => setDefaultToolId(e.target.value)} className="input-pro mt-2">
              <option value="">Use tool_slug/tool_id from import</option>
              {tools.map((tool) => (
                <option key={tool.id} value={tool.id}>{tool.name}</option>
              ))}
            </select>
          </label>
        </div>
        <textarea
          rows="9"
          value={importContent}
          onChange={(e) => setImportContent(e.target.value)}
          className="input-pro w-full font-mono text-xs"
          placeholder={importFormat === 'json'
            ? '[{"tool_slug":"notion","author_name":"Jane","rating":5,"title":"Excellent","body":"Very easy to deploy."}]'
            : 'tool_slug,author_name,rating,title,body,role_profile,company_size,source_label'}
        />
      </div>

      <div className="terminal-grid p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Remote Review Sources</h3>
            <p className="text-sm mt-1 text-text-secondary">Save public CSV/JSON export URLs from review platforms or aggregators, then sync them manually or on the weekly cadence you choose.</p>
          </div>
          <button type="button" onClick={saveSyncSource} className="btn-primary">{sourceForm.id ? 'Update Source' : 'Save Source'}</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
          <label className="text-sm text-text-secondary">
            Source name
            <input value={sourceForm.name} onChange={(e) => setSourceForm((prev) => ({ ...prev, name: e.target.value }))} className="input-pro mt-2" placeholder="G2 weekly export" />
          </label>
          <label className="text-sm text-text-secondary">
            Provider
            <select value={sourceForm.provider} onChange={(e) => setSourceForm((prev) => ({ ...prev, provider: e.target.value }))} className="input-pro mt-2">
              <option value="g2">G2</option>
              <option value="capterra">Capterra</option>
              <option value="trustpilot">Trustpilot</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <label className="text-sm text-text-secondary">
            Format
            <select value={sourceForm.source_format} onChange={(e) => setSourceForm((prev) => ({ ...prev, source_format: e.target.value }))} className="input-pro mt-2">
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
          </label>
          <label className="text-sm text-text-secondary md:col-span-2 xl:col-span-3">
            Public source URL
            <input value={sourceForm.source_url} onChange={(e) => setSourceForm((prev) => ({ ...prev, source_url: e.target.value }))} className="input-pro mt-2" placeholder="https://example.com/reviews-export.csv" />
          </label>
          <label className="text-sm text-text-secondary">
            Source label
            <input value={sourceForm.source_label} onChange={(e) => setSourceForm((prev) => ({ ...prev, source_label: e.target.value }))} className="input-pro mt-2" placeholder="Displayed as source" />
          </label>
          <label className="text-sm text-text-secondary">
            Tool target
            <select value={sourceForm.tool_id} onChange={(e) => setSourceForm((prev) => ({ ...prev, tool_id: e.target.value }))} className="input-pro mt-2">
              <option value="">Resolve from import data</option>
              {tools.map((tool) => (
                <option key={tool.id} value={tool.id}>{tool.name}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-text-secondary">
            Sync every N days
            <input type="number" min="1" max="30" value={sourceForm.sync_interval_days} onChange={(e) => setSourceForm((prev) => ({ ...prev, sync_interval_days: Number(e.target.value) || 7 }))} className="input-pro mt-2" />
          </label>
          <label className="text-sm text-text-secondary inline-flex items-center gap-2 xl:col-span-3">
            <input type="checkbox" checked={!!sourceForm.auto_sync_enabled} onChange={(e) => setSourceForm((prev) => ({ ...prev, auto_sync_enabled: e.target.checked }))} />
            Enable recurring sync for this source
          </label>
        </div>

        {syncSources.length === 0 ? (
          <p className="text-text-secondary">No remote review sources configured yet.</p>
        ) : (
          <div className="space-y-3">
            {syncSources.map((source) => (
              <div key={source.id} className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-semibold text-white">{source.name} <span className="text-text-muted text-sm">({source.provider || 'custom'})</span></div>
                    <div className="text-xs text-text-secondary mt-1">{source.source_url}</div>
                    <div className="text-xs text-text-muted mt-2">Last sync: {source.last_synced_at || 'Never'} - Next sync: {source.next_sync_at || 'Manual only'} - Status: {source.last_sync_status || 'Not run'}</div>
                    {source.last_sync_message && <div className="text-xs text-text-secondary mt-1">{source.last_sync_message}</div>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={() => editSource(source)} className="btn-secondary text-xs px-3 py-1">Edit</button>
                    <button type="button" onClick={() => runSourceSync(source.id)} className="btn-primary text-xs px-3 py-1">{syncingSourceId === source.id ? 'Syncing...' : 'Sync Now'}</button>
                    <button type="button" onClick={() => deleteSource(source.id)} className="btn-secondary text-xs px-3 py-1">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="terminal-grid p-6">
        <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>Generate AI Summary per Tool</h3>
        <div className="flex flex-wrap gap-2">
          {tools.slice(0, 24).map((tool) => (
            <button key={tool.id} onClick={() => runToolSummary(tool.id)} className="btn-secondary text-xs px-3 py-1">
              Summarize {tool.name}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Tools Tab
const ToolsTab = ({ tools, onAdd, onEdit, onRefreshFromSource, onDelete, onImportJson, onBulkImportJson, onExportJson, onDownloadSourceJson, refreshingToolIds = [] }) => {
  const jsonFileInputRef = React.useRef(null);
  const bulkJsonFileInputRef = React.useRef(null);
  const [toolActionBusy, setToolActionBusy] = useState({});

  const runToolAction = async (toolId, action, actionRunner) => {
    if (!toolId || !actionRunner) return;
    setToolActionBusy((prev) => ({ ...prev, [toolId]: action }));
    try {
      await actionRunner();
    } finally {
      setToolActionBusy((prev) => {
        const next = { ...prev };
        delete next[toolId];
        return next;
      });
    }
  };

  const getToolActionState = (tool) => {
    const action = toolActionBusy[tool.id] || '';
    const refreshing = refreshingToolIds.includes(tool.id);
    return {
      action,
      refreshing,
      locked: refreshing || !!action,
    };
  };

  return (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
    <div className="flex items-center justify-between mb-8">
      <div>
        <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Tools</h2>
        <p className="text-xs text-text-muted mt-1">Export = full validated record &nbsp;·&nbsp; Source JSON = raw scrape snapshot for round-trip editing</p>
      </div>
      <div className="flex gap-2">
        <button onClick={onAdd} className="btn-primary inline-flex items-center gap-2" data-testid="add-tool-button">
          <Plus size={20} /> Add Tool
        </button>
        <button
          onClick={() => jsonFileInputRef.current?.click()}
          className="btn-secondary inline-flex items-center gap-2"
          data-testid="import-tool-json-button"
          title="Import or update a tool from a JSON file"
        >
          <Upload size={20} /> Import JSON
        </button>
        <button
          onClick={() => bulkJsonFileInputRef.current?.click()}
          className="btn-secondary inline-flex items-center gap-2"
          data-testid="bulk-import-tool-json-button"
          title="Bulk import multiple tool JSON files"
        >
          <Upload size={20} /> Bulk Import JSONs
        </button>
        <input
          ref={jsonFileInputRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.[0]) {
              onImportJson(e.target.files[0]);
              e.target.value = '';
            }
          }}
          data-testid="tool-json-file-input"
        />
        <input
          ref={bulkJsonFileInputRef}
          type="file"
          accept=".json"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) {
              onBulkImportJson(e.target.files);
              e.target.value = '';
            }
          }}
          data-testid="tool-json-files-input"
        />
      </div>
    </div>
    <div className="space-y-3">
      {tools.map(tool => (
        <div key={tool.id} className="terminal-grid p-5 flex items-center justify-between" data-testid={`tool-item-${tool.slug}`}>
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <img
              src={getSafeToolLogoUrl(tool)}
              alt={tool.name}
              className="w-10 h-10 object-contain flex-shrink-0"
              onError={(e) => {
                if (e.currentTarget.src !== `${window.location.origin}${TOOL_LOGO_PLACEHOLDER}`) {
                  e.currentTarget.src = TOOL_LOGO_PLACEHOLDER;
                }
              }}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-white truncate">{tool.name}</h3>
                {tool.last_source_json && Object.keys(tool.last_source_json).length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-cyber-yellow/10 text-cyber-yellow border border-cyber-yellow/30 rounded font-mono flex-shrink-0" title={tool.last_source_json_file_name || 'source snapshot available'}>
                    SRC
                  </span>
                )}
              </div>
              <p className="text-sm text-text-secondary truncate">{tool.tagline}</p>
              <div className="text-xs text-text-muted font-mono mt-1 flex items-center gap-3 flex-wrap">
                <span>Views: {tool.views || 0}</span>
                <span>Clicks: {tool.clicks || 0}</span>
                {tool.affiliate_link?.commission && <span className="text-cyber-yellow">{tool.affiliate_link.commission}</span>}
                {tool.affiliate_link?.my_referral_link && <span className="text-laser-green">Referral link set</span>}
                {tool.last_source_json_file_name && (
                  <span className="text-text-muted truncate max-w-xs" title={tool.last_source_json_file_name}>{tool.last_source_json_file_name}</span>
                )}
                {tool.website_url && (
                  <a
                    href={tool.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300"
                    title="Open official website"
                  >
                    Website
                  </a>
                )}
                {tool.affiliate_link?.url && (
                  <a
                    href={tool.affiliate_link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-laser-green hover:text-emerald-300"
                    title="Open current affiliate URL"
                  >
                    Affiliate Link
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0 ml-4">
            {(() => {
              const { action, refreshing, locked } = getToolActionState(tool);
              return (
                <>
            <button
              onClick={() => runToolAction(tool.id, 'refresh', () => onRefreshFromSource(tool))}
              disabled={locked}
              className="btn-secondary text-sm flex items-center gap-1 disabled:opacity-60"
              title="Update tool data from the official website using scraper safeguards"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Updating...' : 'Update Tool'}
            </button>
            <button onClick={() => onEdit(tool)} disabled={locked} className="btn-secondary text-sm flex items-center gap-1 disabled:opacity-60" title="Edit tool"><Edit size={14} /> Edit</button>
            <button
              onClick={() => runToolAction(tool.id, 'export', () => onExportJson(tool))}
              disabled={locked}
              className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1 disabled:opacity-60"
              title="Export full validated tool record as JSON (safe to re-import)"
            >
              <FileText size={14} /> {action === 'export' ? 'Exporting...' : 'Export'}
            </button>
            {tool.last_source_json && Object.keys(tool.last_source_json).length > 0 && (
              <button
                onClick={() => runToolAction(tool.id, 'source', () => onDownloadSourceJson(tool))}
                disabled={locked}
                className="text-cyber-yellow hover:text-yellow-300 text-sm flex items-center gap-1 disabled:opacity-60"
                title={`Download last scrape snapshot: ${tool.last_source_json_file_name || 'source.json'}`}
                data-testid={`source-json-btn-${tool.id}`}
              >
                <FileText size={14} /> {action === 'source' ? 'Downloading...' : 'Source JSON'}
              </button>
            )}
            <button onClick={() => onDelete(tool.id)} disabled={locked} className="text-red-500 hover:text-red-400 text-sm flex items-center gap-1 disabled:opacity-60" title="Delete tool"><Trash2 size={14} /> Delete</button>
                </>
              );
            })()}
          </div>
        </div>
      ))}
    </div>
  </motion.div>
  );
};

// Category Modal
const CategoryModal = ({ category, onClose, onSave }) => {
  const [formData, setFormData] = useState(category || {
    name: '', slug: '', description: '', meta_title: '', meta_description: '',
    cover_image: '', background_image: '', template: 'default'
  });
  const [saving, setSaving] = useState(false);
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceBusy, setSourceBusy] = useState(false);
  const [sourceMessage, setSourceMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = category
        ? await adminApi.updateCategory(category.id, formData)
        : await adminApi.createCategory(formData);
      onSave(response.data);
    } catch (error) {
      alert('Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const previewFromSource = async () => {
    if (!sourceUrl.trim()) {
      alert('Add a source website URL first');
      return;
    }
    setSourceBusy(true);
    setSourceMessage('Pulling category details from source site...');
    try {
      const response = await adminApi.previewCategoryEnrichment({ website_url: sourceUrl.trim(), name: formData.name || undefined });
      setFormData((current) => normalizeCategoryPayload(response.data?.category || {}, current));
      setSourceMessage(`Preview ready from ${response.data?.scrape?.final_url || sourceUrl.trim()}`);
    } catch (error) {
      setSourceMessage('Category preview failed. Check the source URL and try again.');
      alert(error?.response?.data?.detail || 'Failed to preview category from source');
    } finally {
      setSourceBusy(false);
    }
  };

  const importFromSource = async () => {
    if (!sourceUrl.trim()) {
      alert('Add a source website URL first');
      return;
    }
    setSourceBusy(true);
    setSourceMessage('Importing category from source...');
    try {
      const response = await adminApi.importCategoryFromSource({ website_url: sourceUrl.trim(), name: formData.name || undefined });
      onSave(response.data);
    } catch (error) {
      setSourceMessage('Category import failed.');
      alert(error?.response?.data?.detail || 'Failed to import category from source');
    } finally {
      setSourceBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
      <div className="terminal-grid p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {category ? 'Edit Category' : 'New Category'}
          </h3>
          <button onClick={onClose} className="text-text-muted hover:text-white"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="terminal-grid p-4">
              <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
                <div>
                  <h4 className="text-sm font-semibold text-cyber-yellow">Source Automation</h4>
                  <p className="text-xs text-text-muted mt-1">Preview or one-click import a category from an official vendor or category site.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button type="button" onClick={previewFromSource} disabled={sourceBusy} className="btn-secondary text-xs px-3 py-2">
                    {sourceBusy ? 'Loading...' : 'Preview From Site'}
                  </button>
                  {!category && (
                    <button type="button" onClick={importFromSource} disabled={sourceBusy} className="btn-primary text-xs px-3 py-2">
                      One-Click Import
                    </button>
                  )}
                </div>
              </div>
              <input type="url" value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow"
                placeholder="https://example.com/category-or-vendor-page" />
              {sourceMessage && <p className="text-xs mt-2 text-text-muted">{sourceMessage}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Name</label>
                <input type="text" value={formData.name} required
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" />
              </div>
              <div>
                <label className="block text-text-secondary text-sm mb-2">Slug</label>
                <input type="text" value={formData.slug} required
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" />
              </div>
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Description</label>
              <textarea value={formData.description} required rows="3"
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Template</label>
              <select value={formData.template || 'default'}
                onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                className="w-full px-4 py-3 bg-surface border border-white/20 text-white">
                <option value="default">Default Grid</option>
                <option value="showcase">Showcase (large cards)</option>
                <option value="minimal">Minimal</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Cover Image URL</label>
                <input type="url" value={formData.cover_image || ''}
                  onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                  className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow"
                  placeholder="https://..." />
              </div>
              <div>
                <label className="block text-text-secondary text-sm mb-2">Background Image URL</label>
                <input type="url" value={formData.background_image || ''}
                  onChange={(e) => setFormData({ ...formData, background_image: e.target.value })}
                  className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow"
                  placeholder="https://..." />
              </div>
            </div>
            {(formData.cover_image || formData.background_image) && (
              <div className="flex gap-3">
                {formData.cover_image && <img src={formData.cover_image} alt="Cover" className="w-20 h-14 object-cover rounded border border-white/10" />}
                {formData.background_image && <img src={formData.background_image} alt="Background" className="w-20 h-14 object-cover rounded border border-white/10" />}
              </div>
            )}
          </div>
          <div className="flex gap-4 mt-6">
            <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save Category'}</button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Tool Modal
const ToolModal = ({ tool, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState(() => normalizeToolPayload(tool || {}, TOOL_FORM_DEFAULTS));
  const [saving, setSaving] = useState(false);
  const [galleryInput, setGalleryInput] = useState('');
  const [pricingFeatureDrafts, setPricingFeatureDrafts] = useState([]);
  const [sourceBusy, setSourceBusy] = useState(false);
  const [sourceMessage, setSourceMessage] = useState('');
  const [allowCategoryChange, setAllowCategoryChange] = useState(true);

  useEffect(() => {
    setFormData(normalizeToolPayload(tool || {}, TOOL_FORM_DEFAULTS));
  }, [tool]);

  useEffect(() => {
    const tiers = ensureMinimumPricingTiers(formData.pricing_tiers || [], 3);
    setPricingFeatureDrafts(tiers.map((tier) => (tier.features || []).join(', ')));
    if ((formData.pricing_tiers || []).length !== tiers.length) {
      setFormData((current) => ({ ...current, pricing_tiers: tiers }));
    }
  }, [formData.pricing_tiers]);

  const formatApiError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (!detail) {
      const status = error?.response?.status;
      const message = error?.message;
      if (status) {
        return `${fallback} (HTTP ${status})`;
      }
      if (message) {
        return `${fallback}: ${message}`;
      }
      return fallback;
    }
    if (typeof detail === 'string') return detail;
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  };

  const callToolEnrichmentDirect = async (endpointPath, method = 'POST', payload = null) => {
    const rawBases = [
      process.env.REACT_APP_BACKEND_URL,
      process.env.REACT_APP_API_BASE,
      '/api',
      'http://127.0.0.1:8001',
    ];
    const bases = [...new Set(rawBases.filter(Boolean).map((base) => String(base).trim().replace(/\/$/, '')))];
    const token = localStorage.getItem('authToken') || localStorage.getItem('admin_token');

    let lastError = null;
    for (const base of bases) {
      try {
        const response = await fetch(`/api/admin/tool-enrichment${endpointPath}`, {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: payload ? JSON.stringify(payload) : undefined,
        });

        const data = await response.json().catch(() => ({}));
        if (response.ok) {
          return data;
        }

        const detail = data?.detail || `HTTP ${response.status}`;
        lastError = new Error(String(detail));
        lastError.response = { status: response.status, data };

        if (response.status !== 404) {
          throw lastError;
        }
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('Unable to reach backend tool enrichment endpoint');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const toOptionalNumber = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      const toSafeCount = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : 0;
      };
      const normalizedRating = formData.rating && typeof formData.rating === 'object'
        ? {
          ...formData.rating,
          overall: toOptionalNumber(formData.rating.overall),
          ease_of_use: toOptionalNumber(formData.rating.ease_of_use),
          features: toOptionalNumber(formData.rating.features),
          value: toOptionalNumber(formData.rating.value),
        }
        : null;

      const payload = {
        ...formData,
        pricing_tiers: ensureMinimumPricingTiers(formData.pricing_tiers || [], 3),
        monthly_price_estimate: toOptionalNumber(formData.monthly_price_estimate),
        ranking_score: toOptionalNumber(formData.ranking_score),
        review_count: toSafeCount(formData.review_count),
        reviews_count: toSafeCount(formData.reviews_count),
        rating: normalizedRating?.overall !== null ? normalizedRating : null,
      };
      const response = tool
        ? await adminApi.updateTool(tool.id, payload)
        : await adminApi.createTool(payload);
      onSave(response.data);
    } catch (error) {
      alert(formatApiError(error, 'Failed to save tool'));
    } finally {
      setSaving(false);
    }
  };

  const applyPricingTierUpdate = (index, patch) => {
    const nextTiers = ensureMinimumPricingTiers(formData.pricing_tiers || [], 3);
    nextTiers[index] = { ...nextTiers[index], ...patch };
    setFormData((current) => ({ ...current, pricing_tiers: nextTiers }));
  };

  const addPricingTier = () => {
    const nextTiers = ensureMinimumPricingTiers([...(formData.pricing_tiers || []), { name: '', price: '', features: [], is_popular: false }], 3);
    setFormData((current) => ({ ...current, pricing_tiers: nextTiers }));
  };

  const removePricingTier = (index) => {
    const current = [...(formData.pricing_tiers || [])];
    if (current.length <= 3) return;
    current.splice(index, 1);
    setFormData((data) => ({ ...data, pricing_tiers: ensureMinimumPricingTiers(current, 3) }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file for logo.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (result) {
        setFormData((current) => ({ ...current, logo_url: result }));
      }
    };
    reader.readAsDataURL(file);
  };

  const addGalleryImage = () => {
    if (galleryInput.trim()) {
      setFormData({ ...formData, gallery_images: [...(formData.gallery_images || []), galleryInput.trim()] });
      setGalleryInput('');
    }
  };

  const removeGalleryImage = (idx) => {
    setFormData({ ...formData, gallery_images: formData.gallery_images.filter((_, i) => i !== idx) });
  };

  const previewFromSource = async () => {
    if (!formData.website_url?.trim()) {
      alert('Add the official website URL first');
      return;
    }
    setSourceBusy(true);
    setSourceMessage('Pulling tool details from source site...');
    try {
      const response = await adminApi.previewToolEnrichment({
        website_url: formData.website_url.trim(),
        referral_link: formData.affiliate_link?.my_referral_link || undefined,
        category_id: formData.category_id || undefined,
        tool_name: formData.name || undefined,
      });
      setFormData((current) => normalizeToolPayload(response.data?.tool || {}, current));
      const matchedCategory = response.data?.matched_category || response.data?.tool?.category_name;
      setSourceMessage(`Preview ready from ${response.data?.scrape?.final_url || formData.website_url.trim()}${matchedCategory ? ` · Category: ${matchedCategory}` : ''}`);
    } catch (error) {
      try {
        if (!error?.response || error?.response?.status === 404) {
          const data = await callToolEnrichmentDirect('/tools/preview', 'POST', {
            website_url: formData.website_url.trim(),
            referral_link: formData.affiliate_link?.my_referral_link || undefined,
            category_id: formData.category_id || undefined,
            tool_name: formData.name || undefined,
          });
          setFormData((current) => normalizeToolPayload(data?.tool || {}, current));
          const matchedCategory = data?.matched_category || data?.tool?.category_name;
          setSourceMessage(`Preview ready from ${data?.scrape?.final_url || formData.website_url.trim()}${matchedCategory ? ` · Category: ${matchedCategory}` : ''}`);
          return;
        }
      } catch (fallbackError) {
        setSourceMessage('Tool preview failed.');
        alert(formatApiError(fallbackError, 'Failed to preview tool from source'));
        return;
      }
      setSourceMessage('Tool preview failed.');
      alert(formatApiError(error, 'Failed to preview tool from source'));
    } finally {
      setSourceBusy(false);
    }
  };

  const importFromSource = async () => {
    if (!formData.website_url?.trim()) {
      alert('Add the official website URL first');
      return;
    }
    setSourceBusy(true);
    setSourceMessage('Importing tool from source...');
    try {
      const response = await adminApi.importToolFromSource({
        website_url: formData.website_url.trim(),
        referral_link: formData.affiliate_link?.my_referral_link || undefined,
        category_id: formData.category_id || undefined,
        tool_name: formData.name || undefined,
      });
      onSave(response.data);
    } catch (error) {
      try {
        if (!error?.response || error?.response?.status === 404) {
          const data = await callToolEnrichmentDirect('/tools/import', 'POST', {
            website_url: formData.website_url.trim(),
            referral_link: formData.affiliate_link?.my_referral_link || undefined,
            category_id: formData.category_id || undefined,
            tool_name: formData.name || undefined,
          });
          onSave(data);
          return;
        }
      } catch (fallbackError) {
        setSourceMessage('Tool import failed.');
        alert(formatApiError(fallbackError, 'Failed to import tool from source'));
        return;
      }
      setSourceMessage('Tool import failed.');
      alert(formatApiError(error, 'Failed to import tool from source'));
    } finally {
      setSourceBusy(false);
    }
  };

  const refreshFromSource = async () => {
    if (!tool?.id) {
      return;
    }
    setSourceBusy(true);
    setSourceMessage('Refreshing existing tool from source...');
    try {
      const response = await adminApi.refreshToolFromSource(tool.id, {
        referral_link: formData.affiliate_link?.my_referral_link || undefined,
        allow_category_change: allowCategoryChange,
        minimum_confidence: 0.8,
      });
      onSave(response.data);
    } catch (error) {
      try {
        if (!error?.response || error?.response?.status === 404) {
          const data = await callToolEnrichmentDirect(`/tools/${tool.id}/refresh`, 'PATCH', {
            referral_link: formData.affiliate_link?.my_referral_link || undefined,
            allow_category_change: allowCategoryChange,
            minimum_confidence: 0.8,
          });
          onSave(data);
          return;
        }
      } catch (fallbackError) {
        setSourceMessage('Tool refresh failed.');
        alert(formatApiError(fallbackError, 'Failed to refresh tool from source'));
        return;
      }
      setSourceMessage('Tool refresh failed.');
      alert(formatApiError(error, 'Failed to refresh tool from source'));
    } finally {
      setSourceBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
      <div className="terminal-grid p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {tool ? 'Edit Tool' : 'New Tool'}
          </h3>
          <button onClick={onClose} className="text-text-muted hover:text-white"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 terminal-grid p-4">
              <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
                <div>
                  <h4 className="text-sm font-semibold text-cyber-yellow">Source Automation</h4>
                  <p className="text-xs text-text-muted mt-1">Use the official site to populate the record. Keep your referral link manual.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button type="button" onClick={previewFromSource} disabled={sourceBusy} className="btn-secondary text-xs px-3 py-2">
                    {sourceBusy ? 'Loading...' : 'Preview From Site'}
                  </button>
                  {!tool ? (
                    <button type="button" onClick={importFromSource} disabled={sourceBusy} className="btn-primary text-xs px-3 py-2">
                      One-Click Import
                    </button>
                  ) : (
                    <button type="button" onClick={refreshFromSource} disabled={sourceBusy} className="btn-primary text-xs px-3 py-2">
                      Refresh Existing Tool
                    </button>
                  )}
                </div>
              </div>
              {sourceMessage && <p className="text-xs text-text-muted">{sourceMessage}</p>}
              {tool && (
                <label className="inline-flex items-center gap-2 mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <input
                    type="checkbox"
                    checked={allowCategoryChange}
                    onChange={(e) => setAllowCategoryChange(e.target.checked)}
                  />
                  Allow source category updates on refresh
                </label>
              )}
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Name</label>
              <input type="text" value={formData.name} required
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Slug</label>
              <input type="text" value={formData.slug} required
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-text-secondary text-sm mb-2">Tagline</label>
              <input type="text" value={formData.tagline} required
                onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-text-secondary text-sm mb-2">Description</label>
              <textarea value={formData.description} required rows="3"
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Category</label>
              <select value={formData.category_id} required
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                className="w-full px-4 py-3 bg-surface border border-white/20 text-white">
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Template</label>
              <select value={formData.template || 'default'}
                onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                className="w-full px-4 py-3 bg-surface border border-white/20 text-white">
                <option value="default">Default</option>
                <option value="showcase">Showcase</option>
                <option value="minimal">Minimal</option>
                <option value="comparison">Comparison</option>
              </select>
            </div>
            <div className="md:col-span-2 terminal-grid p-4 mt-2">
              <h4 className="text-sm font-semibold text-cyber-yellow mb-3">Showcase Sync Fields</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Price Summary</label>
                  <input
                    type="text"
                    value={formData.price_summary || ''}
                    onChange={(e) => setFormData({ ...formData, price_summary: e.target.value })}
                    className="w-full px-3 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-cyber-yellow"
                    placeholder="$ 49 /month"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Monthly Price Estimate</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthly_price_estimate ?? ''}
                    onChange={(e) => setFormData({ ...formData, monthly_price_estimate: e.target.value === '' ? '' : Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-cyber-yellow"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Review Count</label>
                  <input
                    type="number"
                    value={formData.review_count ?? 0}
                    onChange={(e) => setFormData({ ...formData, review_count: Number(e.target.value || 0), reviews_count: Number(e.target.value || 0) })}
                    className="w-full px-3 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-cyber-yellow"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Ranking Score</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.ranking_score ?? ''}
                    onChange={(e) => setFormData({ ...formData, ranking_score: e.target.value === '' ? '' : Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-cyber-yellow"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Ranking Tier</label>
                  <input
                    type="text"
                    value={formData.ranking_tier || ''}
                    onChange={(e) => setFormData({ ...formData, ranking_tier: e.target.value })}
                    className="w-full px-3 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-cyber-yellow"
                    placeholder="Leader | Viable | Niche"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Showcase Tabs Available</label>
                  <div className="w-full min-h-[42px] px-3 py-2 bg-transparent border border-white/20 text-text-secondary text-sm">
                    {Object.keys(formData.showcase_tabs || {}).length
                      ? Object.keys(formData.showcase_tabs || {}).sort().join(', ')
                      : 'No tabs mapped yet'}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Website URL</label>
              <input type="url" value={formData.website_url} required
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Logo URL</label>
              <input type="url" value={formData.logo_url || ''}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" />
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Manual logo upload:</label>
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="text-xs" />
              </div>
            </div>

            <div className="md:col-span-2 terminal-grid p-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-cyber-yellow">Pricing Plans (Dual Method)</h4>
                <button type="button" onClick={addPricingTier} className="btn-secondary text-xs px-3 py-1">Add Plan</button>
              </div>
              <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                Source refresh fills plans automatically when available. If not, enter plans manually. New tools keep at least 3 plans.
              </p>
              <div className="space-y-3">
                {ensureMinimumPricingTiers(formData.pricing_tiers || [], 3).map((tier, idx) => (
                  <div key={`plan-${idx}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                    <input
                      type="text"
                      value={tier.name}
                      onChange={(e) => applyPricingTierUpdate(idx, { name: e.target.value })}
                      className="md:col-span-3 px-3 py-2 bg-transparent border border-white/20 text-white text-sm"
                      placeholder="Plan name"
                    />
                    <input
                      type="text"
                      value={tier.price}
                      onChange={(e) => applyPricingTierUpdate(idx, { price: e.target.value })}
                      className="md:col-span-2 px-3 py-2 bg-transparent border border-white/20 text-white text-sm"
                      placeholder="$29/mo"
                    />
                    <input
                      type="text"
                      value={pricingFeatureDrafts[idx] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPricingFeatureDrafts((drafts) => {
                          const copy = [...drafts];
                          copy[idx] = value;
                          return copy;
                        });
                        applyPricingTierUpdate(idx, {
                          features: value.split(',').map((item) => item.trim()).filter(Boolean),
                        });
                      }}
                      className="md:col-span-5 px-3 py-2 bg-transparent border border-white/20 text-white text-sm"
                      placeholder="Features (comma separated)"
                    />
                    <div className="md:col-span-2 flex items-center justify-between">
                      <label className="inline-flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        <input
                          type="checkbox"
                          checked={!!tier.is_popular}
                          onChange={(e) => applyPricingTierUpdate(idx, { is_popular: e.target.checked })}
                        />
                        Popular
                      </label>
                      <button type="button" onClick={() => removePricingTier(idx)} className="text-xs px-2 py-1 border border-white/20 text-text-muted hover:text-white" disabled={(formData.pricing_tiers || []).length <= 3}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Media Section */}
            <div className="md:col-span-2 terminal-grid p-4 mt-2">
              <h4 className="text-sm font-semibold text-cyber-yellow mb-3">Media & Visual</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Cover Image URL</label>
                  <input type="url" value={formData.cover_image || ''}
                    onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                    className="w-full px-3 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-cyber-yellow" placeholder="Hero/cover image" />
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Background Image URL</label>
                  <input type="url" value={formData.background_image || ''}
                    onChange={(e) => setFormData({ ...formData, background_image: e.target.value })}
                    className="w-full px-3 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-cyber-yellow" placeholder="Page background" />
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Video URL (YouTube)</label>
                  <input type="url" value={formData.video_url || ''}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    className="w-full px-3 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-cyber-yellow" placeholder="https://youtube.com/watch?v=..." />
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Video Embed HTML</label>
                  <input type="text" value={formData.video_embed || ''}
                    onChange={(e) => setFormData({ ...formData, video_embed: e.target.value })}
                    className="w-full px-3 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-cyber-yellow" placeholder="<iframe ...>" />
                </div>
              </div>
              {/* Gallery */}
              <div className="mt-3">
                <label className="block text-text-secondary text-xs mb-1">Gallery Images</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {(formData.gallery_images || []).map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img} alt="" className="w-16 h-12 object-cover rounded border border-white/10" />
                      <button type="button" onClick={() => removeGalleryImage(i)}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input type="url" value={galleryInput} onChange={(e) => setGalleryInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGalleryImage(); } }}
                    className="flex-1 px-3 py-2 bg-transparent border border-white/20 text-white text-sm" placeholder="Add image URL..." />
                  <button type="button" onClick={addGalleryImage} className="btn-secondary text-xs px-3">Add</button>
                </div>
              </div>
            </div>

            {/* Affiliate Section */}
            <div className="md:col-span-2">
              <label className="block text-text-secondary text-sm mb-2 text-cyber-yellow">My Referral Link</label>
              <input type="url" value={formData.affiliate_link?.my_referral_link || ''}
                onChange={(e) => setFormData({ ...formData, affiliate_link: { ...formData.affiliate_link, my_referral_link: e.target.value } })}
                className="w-full px-4 py-3 bg-transparent border-2 border-cyber-yellow/50 text-white focus:border-cyber-yellow"
                placeholder="https://your-affiliate-link.com/ref/YOUR_ID" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-text-secondary text-sm mb-2">Official Signup URL</label>
              <input type="url" value={formData.affiliate_link?.signup_url || formData.affiliate_link?.url || ''}
                onChange={(e) => setFormData({ ...formData, affiliate_link: { ...formData.affiliate_link, signup_url: e.target.value, url: e.target.value } })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" required />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Commission</label>
              <input type="text" value={formData.affiliate_link?.commission || ''}
                onChange={(e) => setFormData({ ...formData, affiliate_link: { ...formData.affiliate_link, commission: e.target.value } })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" placeholder="30% Recurring" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Network</label>
              <input type="text" value={formData.affiliate_link?.network || ''}
                onChange={(e) => setFormData({ ...formData, affiliate_link: { ...formData.affiliate_link, network: e.target.value } })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow" placeholder="PartnerStack" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-text-secondary text-sm mb-2">Disclosure Override Text</label>
              <input
                type="text"
                value={formData.affiliate_link?.disclosure_text_override || ''}
                onChange={(e) => setFormData({ ...formData, affiliate_link: { ...formData.affiliate_link, disclosure_text_override: e.target.value } })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow"
                placeholder="Custom disclosure text when exact rates cannot be published"
              />
            </div>
            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <input
                  type="checkbox"
                  checked={formData.affiliate_link?.show_commission_public !== false}
                  onChange={(e) => setFormData({
                    ...formData,
                    affiliate_link: {
                      ...formData.affiliate_link,
                      show_commission_public: e.target.checked,
                    },
                  })}
                />
                Show commission publicly
              </label>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Turn this off if a partner program restricts sharing exact rates. Public pages will show generic disclosure text.
              </p>
            </div>
          </div>
          <div className="flex gap-4 mt-6">
            <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save Tool'}</button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Standalone Content Generator
const ContentGenerator = () => {
  const [topic, setTopic] = useState('');
  const [contentType, setContentType] = useState('blog_post');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [generatedContent, setGeneratedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState(null);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await adminApi.generateContent({ topic, content_type: contentType, tone, length });
      setGeneratedContent(response.data.content);
    } catch (error) {
      setErrorModal({
        title: 'Content Generation Failed',
        message: error?.response?.data?.detail || error?.message || 'Failed to generate content',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <h2 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Unbounded, sans-serif' }}>AI Content Generator</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="terminal-grid p-6">
          <form onSubmit={handleGenerate}>
            <div className="space-y-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Topic</label>
                <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)} required
                  className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow"
                  placeholder="e.g., AI Image Generators" data-testid="content-gen-topic-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Type</label>
                  <select value={contentType} onChange={(e) => setContentType(e.target.value)}
                    className="w-full px-3 py-2 bg-surface border border-white/20 text-white text-sm">
                    <option value="blog_post">Blog Post</option>
                    <option value="meta_captions">Social Captions</option>
                    <option value="youtube_script">YouTube Script</option>
                    <option value="tool_description">Tool Review</option>
                    <option value="comparison">Comparison</option>
                    <option value="newsletter">Newsletter</option>
                  </select>
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Tone</label>
                  <select value={tone} onChange={(e) => setTone(e.target.value)}
                    className="w-full px-3 py-2 bg-surface border border-white/20 text-white text-sm">
                    <option value="professional">Professional</option>
                    <option value="casual">Casual</option>
                    <option value="technical">Technical</option>
                    <option value="persuasive">Persuasive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-text-secondary text-xs mb-1">Length</label>
                  <select value={length} onChange={(e) => setLength(e.target.value)}
                    className="w-full px-3 py-2 bg-surface border border-white/20 text-white text-sm">
                    <option value="short">Short</option>
                    <option value="medium">Medium</option>
                    <option value="long">Long</option>
                  </select>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="generate-content-button">
                {loading ? 'Generating...' : 'Generate Content'}
              </button>
            </div>
          </form>
        </div>
        <div className="terminal-grid p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Generated Content</h3>
          {generatedContent ? (
            <div className="bg-surface p-4 text-text-secondary whitespace-pre-wrap max-h-96 overflow-y-auto font-mono text-sm">
              {generatedContent}
            </div>
          ) : (
            <div className="text-center text-text-muted py-12">
              <Sparkles size={48} className="mx-auto mb-4 opacity-50" />
              <p>Generated content will appear here</p>
            </div>
          )}
        </div>
      </div>

      {errorModal && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-6" data-testid="error-detail-modal">
          <div className="terminal-grid p-6 max-w-2xl w-full" style={{
            background: errorModal.type === 'error' ? '#1a0000' : errorModal.type === 'success' ? '#001a00' : '#000d1a',
            borderColor: errorModal.type === 'error' ? '#ff4444' : errorModal.type === 'success' ? '#44ff44' : '#4488ff',
          }}>
            <h3 className="text-xl font-bold mb-3" style={{
              color: errorModal.type === 'error' ? '#ff6666' : errorModal.type === 'success' ? '#66ff66' : '#6699ff',
            }}>
              {errorModal.title}
            </h3>
            {errorModal.toolName && (
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                <strong>Tool:</strong> {errorModal.toolName}
              </p>
            )}
            {errorModal.statusCode && (
              <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
                <strong>Status Code:</strong> {errorModal.statusCode}
              </p>
            )}
            <div className="bg-black/50 p-4 rounded text-sm font-mono mb-4 overflow-auto max-h-48" style={{
              color: errorModal.type === 'error' ? '#ff9999' : 'var(--text-secondary)',
            }}>
              {errorModal.message}
            </div>
            <button
              onClick={() => setErrorModal(null)}
              className="btn-primary w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminDashboard;

